import { FormField, fieldA11y } from '@/components/FormField';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/ui/input';
import { OptionSelect } from '@/components/OptionSelect';
import { repoError, repoHint, repoLabel, showsRepoFilter } from './bitriseModel';
import type { BitrisePanelState } from './useBitrisePanel';

function ConnectionSelect({ appId, src }: { appId: string; src: BitrisePanelState['src'] }) {
  return (
    <FormField id={`bitrise-connection-${appId}`} label="Bitbucket workspace">
      <OptionSelect
        id={`bitrise-connection-${appId}`}
        value={src.connection?.id ?? ''}
        onValueChange={src.selectConnection}
        placeholder="Pick"
        options={src.usable.map((c) => ({ value: c.id, label: `${c.name} · ${c.workspace}` }))}
      />
    </FormField>
  );
}

function AccountSelect({ appId, src }: { appId: string; src: BitrisePanelState['src'] }) {
  return (
    <FormField id={`bitrise-account-${appId}`} label="Bitrise account">
      <OptionSelect
        id={`bitrise-account-${appId}`}
        value={src.account?.id ?? ''}
        onValueChange={src.selectAccount}
        placeholder="Pick"
        options={src.bitriseAccounts.map((a) => ({ value: a.id, label: a.name }))}
      />
    </FormField>
  );
}

function RepositorySelect({ appId, src }: { appId: string; src: BitrisePanelState['src'] }) {
  const { repos } = src;
  return (
    <OptionSelect
      data-testid="bitrise-repository"
      value={src.repository}
      disabled={repos.isLoading}
      onValueChange={src.setRepository}
      placeholder={repos.isLoading ? 'Loading repositories…' : 'Select a repository'}
      options={(repos.data?.data ?? []).map((r) => ({ value: r.slug, label: repoLabel(r) }))}
      {...fieldA11y(`bitrise-repository-${appId}`, repos.isError ? 'error' : undefined, 'hint')}
    />
  );
}

function RepositoryField({ appId, src }: { appId: string; src: BitrisePanelState['src'] }) {
  const { repos } = src;
  return (
    <FormField id={`bitrise-repository-${appId}`} label="Repository" required hint={repoHint(repos.data)} error={repoError(repos.isError, repos.error)}>
      <div className="flex flex-col gap-2">
        {showsRepoFilter(repos.data, src.filter) ? <Input data-testid="bitrise-repository-filter" value={src.filter} onChange={(e) => src.setFilter(e.target.value)} placeholder="Filter by name" autoComplete="off" /> : null}
        <RepositorySelect appId={appId} src={src} />
      </div>
    </FormField>
  );
}

/** The pickers (workspace, Bitrise account, repository) and "Create on Bitrise". */
export function BitriseProvisionForm({ appId, p }: { appId: string; p: BitrisePanelState }) {
  const { src } = p;
  return (
    <div className="flex flex-col gap-3">
      {src.usable.length > 1 ? <ConnectionSelect appId={appId} src={src} /> : null}
      {src.bitriseAccounts.length > 1 ? <AccountSelect appId={appId} src={src} /> : null}
      <RepositoryField appId={appId} src={src} />
      <div>
        <GatedButton size="sm" data-testid="bitrise-create" can={p.admin} blockedReason={!src.repository ? 'Pick the repository first' : undefined} loading={p.prov.provisioning} onClick={p.start}>
          <Icon name="rocket_launch" size={16} />
          Create on Bitrise
        </GatedButton>
      </div>
    </div>
  );
}
